import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext } from 'react';
import { useAuth } from './useAuth';

interface LocalModeContextType {
  isLocalAvailable: boolean;
  checkHealth: () => Promise<boolean>;
  isLoading: boolean;
  error: Error | null;
}

const LocalModeContext = createContext<LocalModeContextType | undefined>(undefined);

export function LocalModeProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();

  // Manual health check function (for backward compatibility)
  const checkHealth = async (): Promise<boolean> => {
    try {
      // Check LMM server health on correct port and endpoint
      const response = await fetch('http://localhost:11443/api/health', {
        signal: AbortSignal.timeout(3000),
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  // Tanstack query for automatic health monitoring
  const {
    data: isLocalAvailable = false,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['local-mode', 'health'],
    queryFn: checkHealth,
    enabled: isAuthenticated && !!user?.hasActiveSubscription, // Only check if user can use local mode
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Check every minute
    refetchIntervalInBackground: true, // Keep checking even when tab is not focused
    retry: 1, // Only retry once for faster feedback
    retryDelay: 5000, // Wait 5 seconds before retry
  });

  return (
    <LocalModeContext.Provider
      value={{
        isLocalAvailable,
        checkHealth,
        isLoading,
        error,
      }}
    >
      {children}
    </LocalModeContext.Provider>
  );
}

export function useLocalMode() {
  const context = useContext(LocalModeContext);
  if (context === undefined) {
    throw new Error('useLocalMode must be used within a LocalModeProvider');
  }
  return context;
}
